import { isRetryableReadError } from './requestLifecycle';

/**
 * Step-by-Step request lifecycle helpers.
 *
 * Every Guided Planning read (canonical status + saved daily plan, and the
 * candidate cards) is owned by the screen that asked for it:
 *   - it can be CANCELLED (AbortController) when a newer request replaces it,
 *     the date or step changes, Back leaves the step, or the screen unmounts;
 *   - it has a finite boundary (GUIDED_REQUEST_TIMEOUT_MS), so a stalled
 *     upstream request ends in a recoverable error instead of an endless
 *     spinner;
 *   - only idempotent GET reads may be retried automatically, and only once.
 *     The candidate POST is never re-sent automatically (explicit Retry only).
 */

// A single Guided request is given up after this long (per request, not per
// transition). A safety net: it never substitutes for a fast backend.
export var GUIDED_REQUEST_TIMEOUT_MS = 15000;

// The predicates are the app-wide shared ones (utils/requestLifecycle.js).
export { isAbortError, isTimeoutError, isRetryableReadError, newRequestController } from './requestLifecycle';

/**
 * Run an idempotent GET read, retrying it once on a retryable transport
 * failure while the signal is still live. `read(opts)` receives
 * { signal, timeout, retry: false } for each attempt: this screen-level retry
 * REPLACES the shared api.js retry for these calls (one retry, never two).
 */
export function readWithOneRetry(read, signal, timeout) {
  var opts = { signal: signal, timeout: timeout || GUIDED_REQUEST_TIMEOUT_MS, retry: false };
  return read(opts).catch(function (err) {
    if (signal && signal.aborted) throw err;
    if (!isRetryableReadError(err)) throw err;
    return read(opts);
  });
}
